import type { CSSProperties, ReactNode } from "react";
import TrafficDots from "./TrafficDots";

export interface TerminalTab {
  id: string;
  label: string;
}

interface TerminalProps {
  /** Mono path label shown after the traffic dots (ignored when `tabs` set). */
  path?: string;
  /** Editor-style file tabs in the title bar — the active tab connects to the
   *  body like a browser tab. Pass `activeTab` + `onTabSelect` with it. */
  tabs?: readonly TerminalTab[];
  activeTab?: string;
  onTabSelect?: (id: string) => void;
  /** Optional right-aligned control slot (response / language toggle). */
  toggle?: ReactNode;
  /** Min body height in px, keeps panes from jumping when content swaps. */
  minHeight?: number;
  className?: string;
  children: ReactNode;
}

/** Dark terminal window chrome. Shared by the LLM showcase & API sections. */
export default function Terminal({
  path,
  tabs,
  activeTab,
  onTabSelect,
  toggle,
  minHeight,
  className = "",
  children,
}: TerminalProps) {
  const bodyStyle: CSSProperties | undefined = minHeight
    ? { minHeight }
    : undefined;

  return (
    <div
      className={`overflow-hidden rounded-ref-lg border border-line-2 bg-[#0d0d12] shadow-ref-lg ${className}`}
    >
      {tabs ? (
        <div className="flex items-center gap-2 border-b border-white/[0.07] bg-[#16161d] px-4">
          <TrafficDots />
          {/* -mb-px on the LIST (not the tabs) keeps the active tab's body
              connection while letting the list scroll horizontally on narrow
              viewports — nowrap tabs would otherwise force the terminal's
              min-content width past small screens.
              Honest toggle buttons, NOT the ARIA tabs pattern (#189): full
              tabs semantics need aria-controls/tabpanel/roving tabindex/
              arrow keys — the same downgrade OwnershipBandSection took. */}
          <div className="-mb-px ml-1.5 flex min-w-0 flex-1 items-end gap-1 self-stretch overflow-x-auto pt-[7px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={t.id === activeTab}
                onClick={() => onTabSelect?.(t.id)}
                className={`whitespace-nowrap rounded-t-[9px] border border-b-0 px-3.5 pb-[9px] pt-[7px] font-jetbrains text-[12px] transition ${
                  t.id === activeTab
                    ? "border-white/[0.07] bg-[#0d0d12] text-[#e6e6f0]"
                    : "border-transparent text-[#73738a] hover:text-[#b6b6c8]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {toggle && <div className="ml-auto py-2">{toggle}</div>}
        </div>
      ) : (
        <div className="flex items-center gap-2 border-b border-white/[0.07] bg-[#16161d] px-4 py-[13px]">
          <TrafficDots />
          {path && (
            <span className="ml-1.5 font-jetbrains text-[12.5px] text-[#73738a]">
              {path}
            </span>
          )}
          {toggle && <div className="ml-auto">{toggle}</div>}
        </div>
      )}
      <div
        className="p-5 font-jetbrains text-[13.5px] leading-[1.75] text-[#d6d6e2]"
        style={bodyStyle}
      >
        {children}
      </div>
    </div>
  );
}
